import { localized } from "@msuite/ui/localization";
import { createColumnHelper } from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { moment } from "@msuite/utils/date";
import { getArticleNumberString } from "@msuite/utils/stock";
import { guardString } from "@msuite/utils/strings";
import type { Enums, TransportOrderListItem } from "@msuite/utils/types";

const columnHelper = createColumnHelper<TransportOrderListItem>();

/** The colour each state carries, matching the outbound transport table so one legend serves both. */
const STATE_COLORS: Record<Enums<"transportOrderState">, "amber" | "blue" | "emerald" | "zinc"> = {
	pending: "amber",
	"in-transit": "blue",
	complete: "emerald",
	canceled: "zinc",
};

/**
 * What one trolley has carried, read as put-aways rather than as transport legs.
 *
 * The outbound table's columns would be empty here — a put-away has no unit, no round and no
 * order — so this is a projection of its own over the same rows. The article and the shelf are the
 * load; the zone pair is not shown, because every put-away starts at the goods-in door and the
 * shelf already says where it ended.
 */
export const columns = [
	columnHelper.accessor("state", {
		id: "state",
		header: localized("general.state"),
		cell: (cell) => (
			<Tag
				color={STATE_COLORS[cell.getValue()]}
				variant="plain"
			>
				{localized(`stock.transport_order_state.${cell.getValue()}`)}
			</Tag>
		),
		size: 140,
	}),
	columnHelper.accessor("articleName", {
		id: "articleName",
		header: localized("stock.article"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor("articleNumber", {
		id: "articleNumber",
		header: localized("stock.article_no"),
		cell: (cell) =>
			getArticleNumberString({
				articleGroupNumber: cell.row.original.articleGroupNumber ?? 0,
				articleProductTypeNumber: cell.row.original.articleProductTypeNumber ?? 0,
				number: cell.getValue() ?? 0,
			}),
		size: 140,
	}),
	columnHelper.accessor("quantity", {
		id: "quantity",
		header: localized("stock.picked_quantity"),
		cell: (cell) => guardString(cell.getValue()),
		size: 120,
	}),
	columnHelper.accessor("articleLocationName", {
		id: "articleLocationName",
		header: localized("stock.put_away_destination"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor("projectName", {
		id: "projectName",
		header: localized("projects.project"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	// Who carried it, and when it was put down. Both null on a leg still waiting to be fetched.
	columnHelper.accessor("pickedUpByName", {
		id: "pickedUpByName",
		header: localized("stock.transport_picked_up_by"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor("completedAt", {
		id: "completedAt",
		header: localized("stock.transport_delivered"),
		cell: (cell) => (cell.getValue() ? moment(cell.getValue()).shortFormat() : "--"),
	}),
];
